import {
  approverFor,
  canManageUsers,
  type ApprovalStage,
  type MeResponse,
  type StageClaim,
  type SubmissionListItem,
} from '@jfh/intake-shared';

/**
 * The application list's views (F10 P2): To do / Done / All (leads: All /
 * Not in iQ), with To do grouped by who has to act.
 */

export type View = 'todo' | 'done' | 'all' | 'not_in_iq';
export type TodoGroup = 'mine' | 'others' | 'fix';

type Me = Pick<MeResponse, 'role' | 'stages'> | null;

const iqFailed = (s: SubmissionListItem) => Boolean(s.iq && !s.iq.ok && !s.iq.stubbed);

/** Whose a stage is. Without stage information, a stage counts as anyone's. */
function claim(me: Me, stage: ApprovalStage): StageClaim {
  return me?.stages?.[stage] ?? 'anyone';
}

/** Which To do group an application is in, or null when it's done. */
export function todoGroup(s: SubmissionListItem, me: Me): TodoGroup | null {
  const acting = approverFor(s.type, s.stage);
  if (acting) return claim(me, acting) === 'others' ? 'others' : 'mine';
  return iqFailed(s) ? 'fix' : null;
}

/** An admin who isn't named for any stage sees one "Waiting for a decision" group. */
export function decidesNothing(me: Me): boolean {
  if (!me || !canManageUsers(me.role)) return false;
  return !Object.values(me.stages ?? {}).includes('mine');
}

const GROUP_LABEL: Record<TodoGroup, string> = {
  mine: 'Waiting for you',
  others: 'Waiting for someone else',
  fix: 'Needs fixing',
};

/** To do: overdue first (most overdue at the top), then longest waiting. */
function todoOrder(a: SubmissionListItem, b: SubmissionListItem): number {
  const ao = Boolean(a.timing?.overdue);
  const bo = Boolean(b.timing?.overdue);
  if (ao !== bo) return ao ? -1 : 1;
  if (ao) return a.timing!.dueAt.localeCompare(b.timing!.dueAt);
  const aw = a.timing?.waitingSince ?? a.createdAt;
  const bw = b.timing?.waitingSince ?? b.createdAt;
  return aw.localeCompare(bw);
}

const newestFirst = (a: SubmissionListItem, b: SubmissionListItem) => b.createdAt.localeCompare(a.createdAt);

export interface ListGroup {
  key: string;
  label: string | null;
  items: SubmissionListItem[];
}

/** The items a view shows, grouped (To do) and sorted. */
export function viewGroups(items: SubmissionListItem[], view: View, me: Me): ListGroup[] {
  if (view === 'todo') {
    const merged = decidesNothing(me);
    const by: Record<TodoGroup, SubmissionListItem[]> = { mine: [], others: [], fix: [] };
    for (const s of items) {
      const g = todoGroup(s, me);
      if (g) by[merged && g !== 'fix' ? 'mine' : g].push(s);
    }
    const order: TodoGroup[] = merged ? ['mine', 'fix'] : ['mine', 'others', 'fix'];
    return order
      .filter((g) => by[g].length)
      .map((g) => ({
        key: g,
        label: merged && g === 'mine' ? 'Waiting for a decision' : GROUP_LABEL[g],
        items: by[g].sort(todoOrder),
      }));
  }
  const pick =
    view === 'done'
      ? items.filter((s) => todoGroup(s, me) === null)
      : view === 'not_in_iq'
        ? items.filter((s) => !(s.iq?.ok && !s.iq.stubbed))
        : items;
  return [{ key: view, label: null, items: [...pick].sort(newestFirst) }];
}

/** How many each view would show. */
export function viewCounts(items: SubmissionListItem[], me: Me): Record<View, number> {
  const todo = items.filter((s) => todoGroup(s, me) !== null).length;
  return {
    todo,
    done: items.length - todo,
    all: items.length,
    not_in_iq: items.filter((s) => !(s.iq?.ok && !s.iq.stubbed)).length,
  };
}

/** "1 waiting for you · 1 overdue · 2 need fixing" */
export function summaryLine(items: SubmissionListItem[], me: Me): string {
  const merged = decidesNothing(me);
  let mine = 0;
  let overdue = 0;
  let fix = 0;
  for (const s of items) {
    const g = todoGroup(s, me);
    if (g === 'mine' || (merged && g === 'others')) mine += 1;
    if (g === 'fix') fix += 1;
    if (g && g !== 'fix' && s.timing?.overdue) overdue += 1;
  }
  const parts = [
    merged ? `${mine} waiting for a decision` : `${mine} waiting for you`,
    ...(overdue ? [`${overdue} overdue`] : []),
    ...(fix ? [`${fix} ${fix === 1 ? 'needs' : 'need'} fixing`] : []),
  ];
  return parts.join(' · ');
}

/** The menu bubble: waiting for you plus needing fixing (leads: not in iQ). */
export function navCount(items: SubmissionListItem[], me: Me, leads = false): number {
  if (leads) return items.filter((s) => iqFailed(s)).length;
  const merged = decidesNothing(me);
  return items.filter((s) => {
    const g = todoGroup(s, me);
    return g === 'mine' || g === 'fix' || (merged && g === 'others');
  }).length;
}
